/**
 * Вкладка «Новый год» v2 (04.11.2026, владелец: «сейчас нельзя ни удалить заявки, ни как-то понять её статус, предоплату,
 * есть у них квест или нет, ни комментариев нельзя оставить») — модель без React и без api/client:
 *   • статус карточки приходит готовым с сервера (routes/ny/statusRule.ts: Предоплата > Бронь > ручной); здесь — подписи,
 *     фильтры Все / Новые / В работе / Бронь / Предоплата / Отказ / Скрытые со счётчиками и вид карточки под фильтр;
 *   • «Скрытые» — заявки, удалённые из вкладки (routes/ny/hide.ts): в остальных фильтрах их не видно, здесь — из заявок только они
 *     (брони карточки остаются: статус и строка квеста в шапке — правда о клиенте);
 *   • строка квеста свёрнутой карточки (ближайшая бронь) или «квеста нет» с пожеланиями заявки;
 *   • плашка предоплаты карточки и брони, строка последнего комментария.
 * Тесты — nyStatus.test.ts.
 */
import { fmtStamp, matchQuery, nextBooking, type NyBooking, type NyCard, type NyLead, type NyManual, type NyStatus } from "./nyModel";

export const NY_STATUS: Record<NyStatus, string> = { new: "Новая", work: "В работе", booked: "Бронь", prepaid: "Предоплата", refused: "Отказ" };
/** Ручной переключатель в раскрытой карточке. */
export const NY_MANUAL_OPTS: { key: NyManual; label: string }[] = [
  { key: "new", label: "Новая" }, { key: "work", label: "В работе" }, { key: "refused", label: "Отказ" },
];

export type NyFilter = "all" | NyStatus | "hidden";
export const NY_FILTERS: { key: NyFilter; label: string }[] = [
  { key: "all", label: "Все" }, { key: "new", label: "Новые" }, { key: "work", label: "В работе" }, { key: "booked", label: "Бронь" },
  { key: "prepaid", label: "Предоплата" }, { key: "refused", label: "Отказ" }, { key: "hidden", label: "Скрытые" },
];

/** Статус ставится сам (по брони) — ручной переключатель его не перебивает, «Отказ» сервер не примет. */
export const autoStatus = (c: NyCard): boolean => c.status === "booked" || c.status === "prepaid";

/** Во вкладке (не «Скрытые») карточку видно, если у неё есть бронь или не скрытая заявка. */
export const isShown = (c: NyCard): boolean => c.bookings.length > 0 || c.leads.some((l) => !l.hidden);
export const hasHidden = (c: NyCard): boolean => c.leads.some((l) => l.hidden);

/** Карточка под фильтр: «Скрытые» — из заявок только скрытые; остальные — без скрытых, по статусу. Не подходит — null. */
export function viewOf(card: NyCard, f: NyFilter): NyCard | null {
  if (f === "hidden") return hasHidden(card) ? { ...card, leads: card.leads.filter((l) => l.hidden) } : null;
  if (!isShown(card) || (f !== "all" && card.status !== f)) return null;
  return hasHidden(card) ? { ...card, leads: card.leads.filter((l) => !l.hidden) } : card;
}

/** Поиск + фильтр; порядок сервера не трогаем. */
export function filterCards(cards: NyCard[], query: string, f: NyFilter): NyCard[] {
  const out: NyCard[] = [];
  for (const c of cards) {
    if (!matchQuery(c, query)) continue;
    const v = viewOf(c, f);
    if (v) out.push(v);
  }
  return out;
}

/** Счётчики фильтров — с учётом поиска; карточки, не заявки. */
export function filterCounts(cards: NyCard[], query: string): Record<NyFilter, number> {
  const n = Object.fromEntries(NY_FILTERS.map((f) => [f.key, 0])) as Record<NyFilter, number>;
  for (const c of cards) {
    if (!matchQuery(c, query)) continue;
    if (hasHidden(c)) n.hidden++;
    if (!isShown(c)) continue;
    n.all++;
    n[c.status]++;
  }
  return n;
}

export type QuestLine =
  | { kind: "game"; booking: NyBooking; lead: boolean }
  | { kind: "none"; when: string | null; people: number | null; wishes: string | null };

/** Строка квеста свёрнутой карточки: ближайшая предстоящая бронь (бронь главнее лида со слотом); нет — последняя бронь
 *  (уже сыгранная); нет и её — «квеста нет» с пожеланиями свежей заявки карточки. */
export function questLine(card: NyCard, today: string): QuestLine {
  const ahead = (type: string) => nextBooking({ ...card, bookings: card.bookings.filter((b) => b.type === type) }, today);
  const b = ahead("booking") ?? ahead("lead")
    ?? [...card.bookings].filter((x) => x.type === "booking").sort((x, y) => `${y.date} ${y.time}`.localeCompare(`${x.date} ${x.time}`))[0] ?? null;
  if (b) return { kind: "game", booking: b, lead: b.type === "lead" };
  const l: NyLead | undefined = card.leads[0];
  return { kind: "none", when: l?.when ?? null, people: l?.people ?? null, wishes: l?.wishes ?? null };
}

/** «квеста нет · хотят 31 дек вечером · хоррор, ДР» — число людей рисует PlayersBadge рядом. */
export function wishLine(q: { when: string | null; wishes: string | null }, max = 90): string {
  const w = (q.wishes ?? "").replace(/\s+/g, " ").trim();
  return ["квеста нет", q.when ? `хотят ${q.when}` : "", w.length > max ? `${w.slice(0, max).trimEnd()}…` : w].filter(Boolean).join(" · ");
}

/** «2026-12-31» → «31.12» */
export const ddmm = (date: string): string => `${date.slice(8, 10)}.${date.slice(5, 7)}`;
/** 3000 → «3 000 ₽» (неразрывные пробелы) */
export const fmtRub = (n: number): string => `${String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} ₽`;

export interface Pay { paid: boolean; text: string }
/** Плашка предоплаты брони: сумма, «предоплата» без суммы или «без предоплаты»; у лида со слотом — нет плашки. */
export function bookingPay(b: NyBooking): Pay | null {
  if (b.type !== "booking") return null;
  /* канон системы: внесена ⇔ prepaid (как список «БЕЗ предоплаты»); сумма без отметки — не предоплата */
  if (!b.prepaid) return { paid: false, text: "без предоплаты" };
  return { paid: true, text: b.prepaid_amount > 0 ? `предоплата ${fmtRub(b.prepaid_amount)}` : "предоплата, сумма не указана" };
}
/** Плашка предоплаты карточки (рядом со статусом): сумма по броням или «без предоплаты»; без брони — нет плашки. */
export function cardPay(c: NyCard): Pay | null {
  if (c.status === "prepaid") return { paid: true, text: c.prepaid_total > 0 ? fmtRub(c.prepaid_total) : "сумма не указана" };
  return c.status === "booked" ? { paid: false, text: "без предоплаты" } : null;
}

/** Ручной «В работе» / «Отказ» «вернула» новая заявка: почему на экране «Новая» и что вернёт прежний статус. */
export function reopenHint(c: NyCard): string | null {
  if (!c.reopened_from) return null;
  const who = [c.status_by, c.status_at ? fmtStamp(c.status_at) : ""].filter(Boolean).join(", ");
  return `Пришла новая заявка после «${NY_STATUS[c.reopened_from]}»${who ? ` (${who})` : ""} — снова «Новая», ждёт ответа. `
    + `«Обработан» или «Удалить» у заявки вернут «${NY_STATUS[c.reopened_from]}».`;
}

/** Нажатие ручного статуса что-то меняет: другой статус — да; тот же — только если его «вернула» заявка (записать «Новую»). */
export const pickChanges = (c: NyCard, k: NyManual): boolean => k !== c.manual_status || c.reopened_from != null;

/** Подсказка под ручным переключателем, когда статус ставится сам. */
export function autoHint(c: NyCard): string | null {
  if (!autoStatus(c)) return null;
  return `Сейчас «${NY_STATUS[c.status]}» — по брони. Ручной статус покажется, если бронь отменят; «Отказ» — только после отмены брони.`;
}
